'use client'

// VISITOR FACADE (README_CURRENT_TASKS §6): the ONE owner of client visitor
// state — identity, entry dialog open/draft state, and the entry/leave
// operations. Components ask this boundary; they never coordinate provider
// sessions, mount the entry dialog themselves, or duplicate login booleans.
//
// Ownership rules:
// - Identity display/capabilities on a desk stay with the server-derived
//   AccessGrant; this object is who the person is and how they enter/leave.
// - Desk routes ship ZERO next-auth client code on first load: the provider
//   itself is next-auth-free; the gateway imports the session provider lazily
//   inside its operations, and the entry dialog is a lazy client chunk that
//   loads on the first openEntry().
// - refresh='reload' (desk) applies the boot-derived refresh contract: after
//   a successful enter/leave everything identity-derived (access grant, boot
//   payload, pin-chain unlocks, PHP-session bridge) is stale, and a full
//   re-boot is the only honest refresh. The root page ('none') updates in
//   place and owns its own navigation.

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import dynamic from 'next/dynamic'
import type { ILoginEmail, ILoginNamekey } from 'msgdsk-types'
import { visitorGateway } from './gateway.client'
import {
  emptyEntryDraft,
  guestVisitor,
  visitorFromUser,
  visitorHref,
  visitorIsPresent,
  visitorLabel,
  type CurrentVisitor,
  type EntryDraft,
  type VisitorUser,
} from './model'

const AuthModal = dynamic(() => import('./ui/AuthModal'), { ssr: false })

export type VisitorRefreshPolicy = 'reload' | 'none'

export interface VisitorEntryState {
  isOpen: boolean
  draft: EntryDraft
}

export interface VisitorValue extends CurrentVisitor {
  isPresent: boolean
  /** True while the initial session restore has not answered yet — never
   * present that window as a real guest state. */
  isRestoring: boolean
  label: string
  href: string
  entry: VisitorEntryState
  openEntry(): void
  closeEntry(): void
  updateEntry(patch: Partial<EntryDraft>): void
  clearEntry(): void
  enterWithMessage(details: ILoginNamekey): Promise<boolean>
  enterWithEmail(details: ILoginEmail): Promise<boolean>
  leave(options?: { callbackUrl?: string; redirect?: boolean }): Promise<void>
  /** Session-sync primitives for the root restore/promote boundary. */
  remember(user: VisitorUser): void
  forget(): void
}

const VisitorContext = createContext<VisitorValue | null>(null)

export function useVisitorContext(): VisitorValue {
  const value = useContext(VisitorContext)
  if (!value) throw new Error('useVisitor has to be used within <VisitorProvider>')
  return value
}

export function VisitorProvider({
  children,
  initialVisitor,
  refresh = 'none',
  restoring = false,
}: {
  children: React.ReactNode
  /** Server-resolved identity (desk routes) — makes SSR and hydration agree. */
  initialVisitor?: CurrentVisitor | null
  refresh?: VisitorRefreshPolicy
  /** Root page: a client session restore is expected to answer after mount. */
  restoring?: boolean
}) {
  const [current, setCurrent] = useState<CurrentVisitor>(initialVisitor ?? guestVisitor())
  const [isRestoring, setIsRestoring] = useState(restoring && !initialVisitor)
  const [entryOpenState, setEntryOpenState] = useState(false)
  const [entryMounted, setEntryMounted] = useState(false)
  const [draft, setDraft] = useState<EntryDraft>(emptyEntryDraft)

  const remember = useCallback((user: VisitorUser) => {
    setCurrent(visitorFromUser(user))
    setIsRestoring(false)
  }, [])

  const forget = useCallback(() => {
    setCurrent(guestVisitor())
    setIsRestoring(false)
  }, [])

  const openEntry = useCallback(() => {
    setEntryMounted(true)
    setEntryOpenState(true)
  }, [])

  const closeEntry = useCallback(() => {
    setEntryOpenState(false)
  }, [])

  const updateEntry = useCallback((patch: Partial<EntryDraft>) => {
    setDraft(previous => ({ ...previous, ...patch }))
  }, [])

  const clearEntry = useCallback(() => {
    setDraft(emptyEntryDraft())
  }, [])

  const enterWithMessage = useCallback(async (details: ILoginNamekey): Promise<boolean> => {
    const user = await visitorGateway.enterWithMessage(details)
    if (!user) return false
    remember(user)
    setEntryOpenState(false)
    setDraft(emptyEntryDraft())
    if (refresh === 'reload') window.location.reload()
    return true
  }, [refresh, remember])

  const enterWithEmail = useCallback(async (details: ILoginEmail): Promise<boolean> => {
    const user = await visitorGateway.enterWithEmail(details)
    if (!user) return false
    remember(user)
    setEntryOpenState(false)
    setDraft(emptyEntryDraft())
    if (refresh === 'reload') window.location.reload()
    return true
  }, [refresh, remember])

  const leave = useCallback(async (options: { callbackUrl?: string; redirect?: boolean } = {}) => {
    await visitorGateway.leave(options)
    forget()
    if (refresh === 'reload') window.location.reload()
  }, [refresh, forget])

  const value = useMemo<VisitorValue>(() => ({
    ...current,
    isPresent: visitorIsPresent(current),
    isRestoring,
    label: visitorLabel(current),
    href: visitorHref(current),
    entry: { isOpen: entryOpenState, draft },
    openEntry,
    closeEntry,
    updateEntry,
    clearEntry,
    enterWithMessage,
    enterWithEmail,
    leave,
    remember,
    forget,
  }), [current, isRestoring, entryOpenState, draft, openEntry, closeEntry, updateEntry,
    clearEntry, enterWithMessage, enterWithEmail, leave, remember, forget])

  return (
    <VisitorContext.Provider value={value}>
      {children}
      {entryMounted ? <AuthModal /> : null}
    </VisitorContext.Provider>
  )
}
